import { supabase } from '../supabase';

/* ============================================
   LEVELS
   ============================================ */

export const LEVELS = [
  { value: 'junior', label: 'Junior', badgeColor: '#2ea043',
    description: 'Foundational questions, friendly tone' },
  { value: 'mid',    label: 'Mid',    badgeColor: '#00d9ff',
    description: 'Practical scenarios, real-world depth' },
  { value: 'senior', label: 'Senior', badgeColor: '#c9a227',
    description: 'Architecture decisions, trade-offs, leadership' },
  { value: 'staff',  label: 'Staff',  badgeColor: '#9d6eff',
    description: 'Strategy, ambiguity, scaling, vision' },
];

export const LEVEL_BY_VALUE = LEVELS.reduce((acc, l) => {
  acc[l.value] = l;
  return acc;
}, {});

export function coerceLevel(level) {
  if (!level) return 'mid';
  const v = String(level).toLowerCase().trim();
  return LEVEL_BY_VALUE[v] ? v : 'mid';
}

/* ============================================
   PROMPTS
   ============================================ */

export const JD_ANALYSIS_PROMPT = (jdText, skillBlock, profileBlock) => `You are an expert interviewer analyzing a job description against a candidate's skills.

JOB DESCRIPTION:
${jdText}

CANDIDATE'S CURRENT SKILLS:
${skillBlock}${profileBlock}

Analyze the role-skill fit and produce 5 tailored interview questions calibrated to this role's seniority.

Output ONLY valid JSON in this exact format. No markdown fences. No preamble.

{
  "role_title": "<extracted role title>",
  "company": "<company name if in JD, else empty string>",
  "match_score": <integer 0-100>,
  "score_rationale": "<1 sentence why>",
  "skills_aligned": ["<your skill 1 that matches>", "<skill 2>", "<skill 3>", "<skill 4>", "<skill 5>"],
  "gaps": ["<missing/weak area 1 vs JD>", "<gap 2>", "<gap 3>", "<gap 4>", "<gap 5>"],
  "suggested_questions": [
    {"text": "<question 1>", "difficulty": "junior|mid|senior|staff", "type": "behavioral|technical|domain"},
    {"text": "<question 2>", "difficulty": "<level>", "type": "<type>"},
    {"text": "<question 3>", "difficulty": "<level>", "type": "<type>"},
    {"text": "<question 4>", "difficulty": "<level>", "type": "<type>"},
    {"text": "<question 5>", "difficulty": "<level>", "type": "<type>"}
  ],
  "recommended_difficulty": "junior|mid|senior|staff"
}

Score honestly: 90+ exceptional, 70-89 strong, 40-69 some prep needed, 0-39 significant gaps. skills_aligned must use the candidate's actual skill names. Gaps should be specific (not "leadership" but "lacks team-lead experience at >5 reports"). Questions should be specific to this role and probe the gaps.`;

export const INTERVIEWER_SYSTEM_BY_LEVEL = {
  junior: `You are a friendly, warm interviewer evaluating a JUNIOR-level candidate. Look for: solid fundamentals, learning mindset, clear communication, ability to follow through. Be encouraging. Forgive minor knowledge gaps if reasoning is sound.`,
  mid:    `You are a practical interviewer evaluating a MID-level candidate. Look for: real-world experience applying skills, handling complexity, working independently, debugging and decision-making. Expect concrete examples and trade-offs.`,
  senior: `You are a rigorous interviewer evaluating a SENIOR-level candidate. Look for: architecture and design judgment, mentoring others, navigating trade-offs, scope ownership, influence without authority. Expect specific examples, named trade-offs, and second-order thinking.`,
  staff:  `You are an exacting interviewer evaluating a STAFF-level candidate. Look for: strategic thinking, ambiguity tolerance, organizational impact, scaling systems and people, vision and prioritization across multiple teams. Expect named org-wide initiatives, complex trade-offs, and clear evidence of multiplier effect.`,
};

export const ANSWER_FEEDBACK_PROMPT = (question, answer, role, level, skillBlock) => `${INTERVIEWER_SYSTEM_BY_LEVEL[level] || INTERVIEWER_SYSTEM_BY_LEVEL.mid}

ROLE: ${role}
CANDIDATE'S SKILLS:
${skillBlock}

QUESTION: ${question}
CANDIDATE'S ANSWER: ${answer}

Evaluate this answer. Output ONLY valid JSON, no markdown:

{
  "score": <integer 1-5 where 1=poor, 3=ok, 5=excellent>,
  "strength": "<one specific thing they showed, starting with a verb. Reference what they said.>",
  "improvement": "<one specific actionable thing to add or change.>"
}

Be HONEST about score — don't inflate. If the answer is weak, score 1-2. If strong, score 4-5. Strength starts with a verb. Improvement must be actionable.`;

export const FINAL_REPORT_PROMPT = (role, level, qaList, skillBlock, starStories) => `You are generating an Interview Readiness Report for a candidate who just completed mock practice.

ROLE: ${role}
LEVEL TARGETED: ${level}
CANDIDATE'S SKILLS:
${skillBlock}

PRACTICE TRANSCRIPT:
${qaList.map((qa, i) =>
  `Q${i + 1}: ${qa.question}\nAnswer: ${qa.answer}\nEvaluation: score=${qa.score}/5, strength="${qa.strength}", improvement="${qa.improvement}"`
).join('\n\n')}

${starStories && starStories.length > 0
  ? `STAR STORIES THE CANDIDATE PREPARED:\n${starStories.map((s, i) => `Story ${i + 1} (${s.title}):\nS: ${s.situation || '(empty)'}\nT: ${s.task || '(empty)'}\nA: ${s.action || '(empty)'}\nR: ${s.result || '(empty)'}`).join('\n\n')}`
  : 'No STAR stories prepared for this session.'
}

Output ONLY valid JSON, no markdown:

{
  "overall_score": <integer 0-100>,
  "what_you_nailed": ["<strength 1, specific to their answers>", "<strength 2>", "<strength 3>"],
  "where_to_grow": ["<improvement 1>", "<improvement 2>", "<improvement 3>"],
  "top_3_practice": ["<actionable practice item 1>", "<item 2>", "<item 3>"],
  "star_stories_to_rehearse": [<empty array OR 1-3 strings about which STAR stories to polish — only include if STAR stories were provided>]
}

Be specific to THIS candidate's actual answers. Reference what they said. No generic advice.`;

export const STAR_STORY_PROMPT = (role, skillBlock, jdText) => `You are helping a candidate prepare STAR stories for an interview.

TARGET ROLE: ${role}

CANDIDATE'S SKILLS:
${skillBlock}

JOB DESCRIPTION (for context):
${jdText}

Generate 3 STAR story templates the candidate should prepare based on the role and their skills. Each template is a set of prompts (questions) that help the candidate recall and structure a real story from their past.

Output ONLY valid JSON, no markdown:

{
  "stories": [
    {
      "title": "<short title for this story type>",
      "situation_prompt": "<question to help recall the situation>",
      "task_prompt": "<question to define the task>",
      "action_prompt": "<question about what they specifically did>",
      "result_prompt": "<question about measurable outcome>"
    },
    {...story 2...},
    {...story 3...}
  ]
}

Tailor the 3 story types to this role. For senior/staff roles, include scope/influence/cross-team stories. For junior, include learning and collaboration stories.`;

/* ============================================
   SUPABASE
   ============================================ */

export async function loadInterviewSessions(userId) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('skill_pulse_conversations')
    .select('*')
    .eq('user_id', userId)
    .eq('tab', 'interview')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || [])
    .map(row => {
      const parsed = parseSessionOutputSafe(row.output);
      if (!parsed) {
        // eslint-disable-next-line no-console
        console.warn('[SkillPulse Interview] Skipping malformed row', row.id);
        return null;
      }
      return {
        id: row.id,
        created_at: row.created_at,
        conversation: row.conversation,
        metadata: row.metadata || {},
        output: parsed,
      };
    })
    .filter(Boolean);
}

export async function saveSession({ user_id, conversation, output, metadata }) {
  if (!user_id) throw new Error('Missing user_id');
  const payload = {
    user_id,
    tab: 'interview',
    conversation: conversation || [],
    output: JSON.stringify(output || {}),
    metadata: metadata || {},
  };
  const { data, error } = await supabase
    .from('skill_pulse_conversations')
    .insert(payload)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Save didn't write any rows. Check your Supabase RLS policy for INSERT on skill_pulse_conversations.");
  }
  return data[0];
}

/* ============================================
   AI CALLS
   ============================================ */

async function callClaude(prompt, model) {
  const response = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, model }),
  });
  const data = await response.json();
  return (data?.content || []).map(i => i.text || '').join('').trim();
}

export async function analyzeJD(jdText, skillBlock, profileBlock) {
  const text = await callClaude(JD_ANALYSIS_PROMPT(jdText, skillBlock, profileBlock), 'sonnet');
  const parsed = tryParseJSONObject(text);
  validateAnalysis(parsed);
  parsed.recommended_difficulty = coerceLevel(parsed.recommended_difficulty);
  parsed.suggested_questions = (parsed.suggested_questions || []).map(q => ({
    text: String(q.text || '').trim(),
    difficulty: coerceLevel(q.difficulty),
    type: String(q.type || 'behavioral').toLowerCase(),
  }));
  return parsed;
}

export async function evaluateAnswer({ question, answer, role, level, skillBlock }) {
  try {
    const text = await callClaude(
      ANSWER_FEEDBACK_PROMPT(question, answer, role, coerceLevel(level), skillBlock),
      'haiku'
    );
    const parsed = tryParseJSONObject(text);
    if (!parsed || typeof parsed !== 'object') throw new Error('No JSON');
    return {
      score: clampScore(parsed.score),
      strength: String(parsed.strength || 'Clear answer.').trim(),
      improvement: String(parsed.improvement || 'Add a concrete example.').trim(),
    };
  } catch (_) {
    // Practice never stalls — return a neutral fallback
    return {
      score: 3,
      strength: 'Clear answer.',
      improvement: 'Add a concrete example with a measurable outcome.',
    };
  }
}

export async function generateFinalReport({ role, level, qaList, skillBlock, starStories }) {
  const text = await callClaude(
    FINAL_REPORT_PROMPT(role, coerceLevel(level), qaList, skillBlock, starStories || []),
    'sonnet'
  );
  const parsed = tryParseJSONObject(text);
  validateReport(parsed);
  return parsed;
}

export async function generateSTARStories({ role, skillBlock, jdText }) {
  const text = await callClaude(STAR_STORY_PROMPT(role, skillBlock, jdText), 'sonnet');
  const parsed = tryParseJSONObject(text);
  if (!parsed || !Array.isArray(parsed.stories)) {
    throw new Error('STAR generation returned invalid shape');
  }
  return parsed.stories.slice(0, 3).map(s => ({
    title:           String(s.title || 'A story to prepare').trim(),
    situation_prompt: String(s.situation_prompt || '').trim(),
    task_prompt:      String(s.task_prompt || '').trim(),
    action_prompt:    String(s.action_prompt || '').trim(),
    result_prompt:    String(s.result_prompt || '').trim(),
  }));
}

// Per spec — kept for future dynamic-question flows. Not used in V1 (Option A).
export async function generateInterviewQuestion({ role, level, previousAnswers, gaps }) {
  const prompt = `${INTERVIEWER_SYSTEM_BY_LEVEL[coerceLevel(level)]}

ROLE: ${role}
GAPS TO PROBE: ${(gaps || []).join(', ')}
PREVIOUS Q&A:
${(previousAnswers || []).map(p => `Q: ${p.question}\nA: ${p.answer}`).join('\n\n')}

Output ONLY a single interview question, no preamble, no JSON. The question should probe one of the gaps and be calibrated to the level.`;
  return callClaude(prompt, 'haiku');
}

/* ============================================
   HELPERS
   ============================================ */

function tryParseJSONObject(text) {
  if (!text) return null;
  try { return JSON.parse(text); } catch (_) { /* fallthrough */ }
  const match = String(text).match(/\{[\s\S]*\}/);
  if (match) {
    try { return JSON.parse(match[0]); } catch (__) { /* fallthrough */ }
  }
  return null;
}

function parseSessionOutputSafe(text) {
  try {
    if (!text) return null;
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

function validateAnalysis(parsed) {
  if (!parsed || typeof parsed !== 'object') throw new Error('JD analysis returned invalid JSON');
  if (typeof parsed.role_title !== 'string') throw new Error('JD analysis missing role_title');
  if (typeof parsed.match_score !== 'number') throw new Error('JD analysis missing match_score');
  if (!Array.isArray(parsed.skills_aligned)) throw new Error('JD analysis missing skills_aligned');
  if (!Array.isArray(parsed.gaps)) throw new Error('JD analysis missing gaps');
  if (!Array.isArray(parsed.suggested_questions) || parsed.suggested_questions.length === 0) {
    throw new Error('JD analysis missing suggested_questions');
  }
}

function validateReport(parsed) {
  if (!parsed || typeof parsed !== 'object') throw new Error('Report returned invalid JSON');
  if (typeof parsed.overall_score !== 'number') throw new Error('Report missing overall_score');
  if (!Array.isArray(parsed.what_you_nailed)) throw new Error('Report missing what_you_nailed');
  if (!Array.isArray(parsed.where_to_grow)) throw new Error('Report missing where_to_grow');
  if (!Array.isArray(parsed.top_3_practice)) throw new Error('Report missing top_3_practice');
}

function clampScore(s) {
  const n = Math.round(Number(s) || 3);
  if (n < 1) return 1;
  if (n > 5) return 5;
  return n;
}

export function scoreColor(score, max = 100) {
  const pct = (score / max) * 100;
  if (pct >= 90) return '#2ea043';
  if (pct >= 70) return '#00d9ff';
  if (pct >= 40) return '#ffb84d';
  return '#ef4444';
}

export function scoreLabel(score) {
  if (score >= 90) return 'Exceptional alignment';
  if (score >= 70) return 'Strong match';
  if (score >= 40) return 'Good foundation, some prep needed';
  return 'Significant gaps to bridge';
}

export function formatSessionDate(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatReportDate(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  return d.toISOString().slice(0, 10);
}
